import React from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Heading,
  Link as ChakraLink,
  Button,
  Text,
  Badge,
  Container,
  IconButton,
  VStack,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useColorMode,
  useColorModeValue,
} from '@chakra-ui/react';
import { useApp } from '../context/AppContext';

const Layout = () => {
  const { currentUser, logout, canManageUsers } = useApp();
  const location = useLocation();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { colorMode, toggleColorMode } = useColorMode();
  const navBg = useColorModeValue('white', 'gray.800');
  const navBorderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'gray.300');
  const activeBg = useColorModeValue('primary.100', 'primary.900');
  const activeColor = useColorModeValue('primary.600', 'primary.300');
  const hoverBg = useColorModeValue('gray.100', 'gray.700');

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    return location.pathname.startsWith(path);
  };

  const roleLabels = {
    'System Administrator': 'مدير النظام',
    'Examiner': 'خبير',
    'Supervisor': 'مشرف',
    'Read-Only User': 'مستخدم للقراءة فقط',
  };

  // Navigation links
  const navLinks = [
    {
      to: '/cases',
      label: 'القضايا',
      show: true,
    },
    {
      to: '/users',
      label: 'إدارة المستخدمين',
      show: canManageUsers(),
    },
    {
      to: '/locations',
      label: 'إدارة المواقع',
      show: canManageUsers(),
    },
    {
      to: '/audit-logs/search',
      label: 'ملف عمليات القضية',
      show: canManageUsers() && currentUser?.role === 'System Administrator',
    },
    {
      to: '/statistics',
      label: 'الإحصائيات',
      show: currentUser?.role === 'System Administrator' || 
            currentUser?.role === 'Region Supervisor' || 
            currentUser?.role === 'Location Supervisor',
    },
  ].filter(link => link.show);

  return (
    <Box minH="100vh" display="flex" flexDirection="column">
      <Box
        as="nav"
        bg={navBg}
        boxShadow="sm"
        borderBottom="1px solid"
        borderColor={navBorderColor}
        position="sticky"
        top={0}
        zIndex={100}
      >
        <Container maxW="1200px">
          <Flex
            align="center"
            justify="space-between"
            gap={2}
            py={4}
            wrap="nowrap"
          >
            {/* Logo/Title */}
            <Heading size={{ base: 'sm', md: 'md' }} color="primary.600" flexShrink={0}>
              نظام إدارة قضايا البصمات
            </Heading>

            {/* Desktop Navigation - Hidden on mobile */}
            <Flex 
              gap={4} 
              flex={1} 
              justify="center"
              display={{ base: 'none', md: 'flex' }}
            >
              {navLinks.map((link) => (
                <ChakraLink
                  key={link.to}
                  as={Link}
                  to={link.to}
                  px={4}
                  py={2}
                  borderRadius="md"
                  fontWeight="semibold"
                  color={isActive(link.to) ? activeColor : textColor}
                  bg={isActive(link.to) ? activeBg : 'transparent'}
                  whiteSpace="nowrap"
                  _hover={{
                    color: activeColor,
                    bg: hoverBg,
                  }}
                >
                  {link.label}
                </ChakraLink>
              ))}
            </Flex>

            {/* User Info and Actions */}
            <Flex align="center" gap={4} flexShrink={0}>
              {/* Dark Mode Toggle Button */}
              <IconButton
                aria-label={colorMode === 'light' ? 'تفعيل الوضع الداكن' : 'تفعيل الوضع الفاتح'}
                icon={
                  colorMode === 'light' ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )
                }
                onClick={toggleColorMode}
                variant="ghost"
                size="md"
              />
              
              {/* Desktop User Info - Hidden on mobile */}
              <Box display={{ base: 'none', md: 'block' }} whiteSpace="nowrap" flexShrink={0}>
                <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>
                  {currentUser?.fullName}
                </Text>
                <Badge colorScheme="gray" fontSize="xs">
                  {roleLabels[currentUser?.role] || currentUser?.role}
                </Badge>
              </Box>
              
              {/* Desktop Logout Button - Hidden on mobile */}
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                display={{ base: 'none', md: 'block' }}
              >
                تسجيل الخروج
              </Button>

              {/* Mobile Menu Button */}
              <IconButton
                aria-label="القائمة"
                icon={
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                }
                variant="ghost"
                onClick={onOpen}
                display={{ base: 'block', md: 'none' }}
              />
            </Flex>
          </Flex>
        </Container>
      </Box>

      {/* Mobile Drawer Menu */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottom="1px solid" borderColor={navBorderColor}>
            <VStack align="stretch" spacing={2}>
              <Text fontWeight="bold" fontSize="lg">
                {currentUser?.fullName}
              </Text>
              <Badge colorScheme="gray" fontSize="sm" w="fit-content">
                {roleLabels[currentUser?.role] || currentUser?.role}
              </Badge>
            </VStack>
          </DrawerHeader>
          <DrawerBody p={0}>
            <VStack align="stretch" spacing={0}>
              {/* Dark Mode Toggle in Mobile Menu */}
              <Box px={6} py={4} borderBottom="1px solid" borderColor={navBorderColor}>
                <Button
                  onClick={toggleColorMode}
                  variant="ghost"
                  w="full"
                  justifyContent="flex-start"
                  leftIcon={
                    colorMode === 'light' ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )
                  }
                >
                  {colorMode === 'light' ? 'تفعيل الوضع الداكن' : 'تفعيل الوضع الفاتح'}
                </Button>
              </Box>
              {navLinks.map((link) => (
                <ChakraLink
                  key={link.to}
                  as={Link}
                  to={link.to}
                  px={6}
                  py={4}
                  fontWeight="semibold"
                  color={isActive(link.to) ? activeColor : textColor}
                  bg={isActive(link.to) ? activeBg : 'transparent'}
                  borderBottom="1px solid"
                  borderColor={navBorderColor}
                  _hover={{
                    color: activeColor,
                    bg: hoverBg,
                  }}
                  onClick={onClose}
                >
                  {link.label}
                </ChakraLink>
              ))}
              <Box px={6} py={4} borderTop="1px solid" borderColor={navBorderColor} mt="auto">
                <Button
                  onClick={() => {
                    onClose();
                    handleLogout();
                  }}
                  variant="outline"
                  colorScheme="red"
                  w="full"
                >
                  تسجيل الخروج
                </Button>
              </Box>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      <Box as="main" flex={1} py={8}>
        <Container maxW="1200px">
          <Outlet />
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;
